(ns ui.components.tab.view
  "The tabs of ZapKit: a row of underlined tabs. It follows `Tab.tsx` of
   zapkit-components. The classes come from the generated
   `ui.components.tab.zapkit-styles`. The arrow keys, Home and End move the focus
   and select the tab, as in ZapKit."
  (:require
   [reagent.core :as r]
   [ui.components.tab.zapkit-styles :as styles]
   [ui.components.zapkit-variants :as variants]
   [ui.dom-globals :as dg]
   [ui.util :as uu]))

(def svg-classes
  "The classes that ZapKit adds to the icon of a tab."
  "[&_svg]:tw2-h-3 [&_svg]:tw2-w-3 [&_svg]:tw2-shrink-0")

(defn- enabled?
  [item]
  (not (:disabled? item)))

(defn- find-enabled
  "Return the index of the next enabled item from `start`, going in `step`
   (1 or -1), and wrapping around. Return nil when no item is enabled."
  [{:keys [items start step]}]
  (let [item-count (count items)]
    (when (pos? item-count)
      (->> (range 1 (inc item-count))
           (map (fn [distance]
                  (mod (+ start (* step distance) item-count) item-count)))
           (filter (fn [index]
                     (enabled? (nth items index))))
           first))))

(defn target-index
  "Return the index of the tab that a key moves to, or nil when the key does not
   move. `from` is the index of the tab that has the focus. The arrow right and
   arrow down go to the next enabled tab, the arrow left and arrow up go to the
   previous one, Home goes to the first enabled tab and End to the last."
  [{:keys [items from key]}]
  (case key
    ("ArrowRight" "ArrowDown") (find-enabled {:items items
                                              :start from
                                              :step  1})
    ("ArrowLeft" "ArrowUp") (find-enabled {:items items
                                           :start from
                                           :step  -1})
    "Home" (find-enabled {:items items
                          :start -1
                          :step  1})
    "End" (find-enabled {:items items
                         :start (count items)
                         :step  -1})
    nil))

(defn item-class
  "Return the classes of one tab."
  [{:keys [selected? disabled?]}]
  (uu/cm (variants/slot-class (:item styles/tab)
                              {:selected (boolean selected?)
                               :disabled (boolean disabled?)})
         svg-classes))

(defn tablist
  "The markup of the tabs. `nodes*` is an atom that keeps the node of each tab,
   so that a key can move the focus. `view` makes it. See `view` for the other
   props."
  [{:keys [items value on-change aria-label class nodes*]}]
  (let [items (vec items)
        select-index! (fn [index]
                        (let [item (get items index)]
                          (when (and item
                                     (enabled? item))
                            (dg/focus! (get @nodes* index))
                            (when (not= (:value item) value)
                              (on-change (:value item))))))]
    [:div
     {:role       "tablist"
      :aria-label aria-label
      :class      (uu/cm (get-in styles/tab [:root :base])
                         class)}
     (map-indexed
      (fn [index {item-value :value :keys [label disabled? icon]}]
        (let [selected? (= item-value value)]
          ^{:key (str item-value)}
          [:button
           {:ref           (fn [node]
                             (swap! nodes* assoc index node))
            :type          "button"
            :role          "tab"
            :aria-selected selected?
            :aria-disabled (when disabled? true)
            :disabled      (boolean disabled?)
            :tab-index     (if selected? 0 -1)
            :class         (item-class {:selected? selected?
                                        :disabled? disabled?})
            :on-click      (fn [_]
                             (when-not disabled?
                               (on-change item-value)))
            :on-key-down   (fn [event]
                             (when-let [next-index (target-index {:items items
                                                                  :from  index
                                                                  :key   (dg/event-key event)})]
                               (dg/prevent-default event)
                               (select-index! next-index)))}
           icon
           label]))
      items)]))

(defn view
  "Render tabs.

   `:items` is a vector of maps with `:value`, `:label`, and optional
   `:disabled?` and `:icon`.
   `:value` is the value of the selected tab.
   `:on-change` is called with the value of the tab that the user selects.
   `:aria-label` names the row of tabs for a screen reader.
   `:class` is added to the row."
  [props]
  (r/with-let [nodes* (atom {})]
    [tablist (assoc props :nodes* nodes*)]))
